import {
    action,
    computed,
    makeObservable,
    observable,
    override,
    reaction,
} from "mobx";
import { v4 as uuidv4 } from "uuid";
import { SignalTableDatum } from "models/Reporting/ReportDatum";
import { RenderModeType } from "../../SignalsModels";
import * as SignalsModels from "../../SignalsModels";
import type {
    BaseObjectStoreParams,
    BaseRefreshOptions,
    RefreshResult,
} from "../BaseObjectStore";
import BaseObjectStore from "../BaseObjectStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { toGridColDef } from "./formatterRegistry";
import OrgOptionsStore from "stores/OrgOptionsStore";
import { getEndpointBehaviorConfig } from "../_DataEndpointConfig";
import type { EndpointBehaviorConfig } from "../_DataEndpointConfig/Types";
import { resolveSelectionDefaults } from "../_DataEndpointConfig/DefaultSelectionsSetters";
import type {
    SelectionDefaultDependency,
    SelectionDefaultsResolverContext,
} from "../_DataEndpointConfig/DefaultSelectionsSetters";
import type {
    SignalsTemporalLabels,
    ISignalsChartDefinition,
    ISignalsSelectionTypes,
    SignalsParsedVizOptions,
    SignalsRequest,
    SignalsTableResponse,
} from "../../SignalsModels";
import { temporalGrain, THE_MICHAEL_GRAIN } from "../DataAggregation";
import { toIsoByDateReference } from "utils/DateTimeUtils";
import moment from "moment";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import type { FieldNode } from "components/CSVExport/fieldNodes";

type TableCachePayload = {
    rows: SignalTableDatum[];
    columns: IColDef[];
    pagination: SignalsModels.SignalsTablePagination;
    sqlQuery?: string;
};

class TableCardStore extends BaseObjectStore<
    SignalTableDatum[],
    SignalsRequest | null,
    TableCachePayload
> {
    @observable
    lastResponse?: SignalsTableResponse;

    /** Anchor position for the point menu popover, set by drilldown cell click. null = closed. */
    @observable pointMenuAnchor: { top: number; left: number } | null = null;

    /** Object/array cell value being viewed in the JSON viewer dialog. null = closed. */
    @observable jsonCellViewer: {
        isOpen: boolean;
        title: string;
        value: unknown;
    } = { isOpen: false, title: "", value: "" };

    @observable selectionType: ISignalsSelectionTypes;
    @observable currentRenderMode: RenderModeType = RenderModeType.Count;
    @observable dataGridStore: AcxDataGridStore | null = null;
    @observable selectedIds: Record<
        SelectedIdKey,
        string | string[] | undefined
    > = {
        extractionLlmClassifier: undefined,
        extendedMetadataObjectClassifier: undefined,
    };
    @observable sqlQuery?: string;
    @observable comparePrevEnabled: boolean = false;
    /** True while the card is being captured for PNG export: the grid renders every row on the page, without scrolling. */
    @observable isPngCaptureInProgress: boolean = false;
    @observable
    private waitingSelectionDefaultDependencies: SelectionDefaultDependency[] =
        [];
    private requestedSelectionDefaultDependencies: Set<SelectionDefaultDependency> =
        new Set();
    private selectionDefaultDependencyPromises = new Map<
        SelectionDefaultDependency,
        Promise<void>
    >();
    private pendingStandaloneDefaultFollowup: Promise<void> | null = null;
    private hasResolvedInitialSelectionDefaults: boolean = false;
    private suppressDefaultPersistence: boolean = false;

    config: EndpointBehaviorConfig;
    optionsStore: OrgOptionsStore;

    constructor(
        params: BaseObjectStoreParams<
            SignalTableDatum[],
            ISignalsChartDefinition
        >,
    ) {
        const shouldRefreshOnCreate = params.refreshOnCreate ?? false;
        super({
            ...params,
            refreshOnCreate: false,
        });
        makeObservable(this);

        if (!this.dataEndpoint) {
            throw new Error(
                `TableCardStore must have a valid data endpoint. Card ID: ${this.id}`,
            );
        }

        this.optionsStore = this.rootStore.getStore(OrgOptionsStore);
        this.selectionType =
            SignalsModels.DefinitionDataSelectionTypeMapping[
                params.baseObject.typeOfDataSelectedInChart
            ];

        this.config = getEndpointBehaviorConfig({
            dataEndpoint: this.dataEndpoint,
            selectionType: this.selectionType,
        });

        this.dataGridStore = new AcxDataGridStore("tbl-" + this.id);
        this.dataGridStore.removeHeight = "54px";
        this.configureGrid();
        this.initializeSelectionDefaults();
        reaction(
            () => JSON.stringify(this.getSelectedIdsForVizDefaults()),
            () => {
                void this.updateDefaultSelectedIds();
            },
            { delay: 500 },
        );
        if (shouldRefreshOnCreate) {
            void this.runRefresh();
        }
    }

    @computed
    get isWaitingOnRequiredOptions() {
        switch (this.selectionType) {
            case "Classifiers":
                return (
                    this.optionsStore.classifierCategoriesLoading ||
                    this.optionsStore.userClassifiersLoading
                );
            case "Question":
            case "Questions":
            case "Tags":
                return this.optionsStore.orgModulesLoading;
            case "Modules":
            case "Module":
                return this.optionsStore.orgModulesLoading;
            case "Topics":
                return this.optionsStore.topicsLoading;
            case "LLM Classifier":
            case "LLM Classifier Results":
                return this.optionsStore.llmClassifiersLoading;
            case "LLM Classifier Extracted Metadata":
                return (
                    this.optionsStore.extractionLlmClassifiersLoading ||
                    this.optionsStore.extendedMetadataObjectClassifiersLoading
                );
            default:
                return false;
        }
    }

    @computed
    get isWaitingOnSelectionDefaults() {
        return this.waitingSelectionDefaultDependencies.length > 0;
    }

    @override
    override async runRefresh(
        forceRefresh: boolean = false,
        options?: BaseRefreshOptions,
    ): Promise<RefreshResult> {
        const selectionResolution = this.resolveSelectionDefaultsForRefresh();
        const refreshOrigin = options?.origin ?? "standalone";

        if (selectionResolution.status === "waiting") {
            if (refreshOrigin === "dashboard-batch") {
                return {
                    status: "deferred",
                    waitFor: selectionResolution.waitFor,
                };
            }

            if (this.canAutoRefreshInCurrentContext()) {
                this.scheduleStandaloneSelectionDefaultsFollowup(
                    selectionResolution.waitFor,
                );
            }
            return { status: "completed" };
        }

        return super.runRefresh(forceRefresh, options);
    }

    public handleSelectionChange() {
        return this.runRefresh();
    }

    @computed get temporalGrain(): SignalsTemporalLabels {
        const start = this.startDate;
        const end = this.endDate;
        if (!start || !end) return "Daily";
        return temporalGrain(start, end);
    }

    @computed get startDate() {
        return this.applicationFiltersStore.startDate;
    }

    @computed get endDate() {
        return this.applicationFiltersStore.endDate;
    }

    @computed get comparePrevDates() {
        const start = this.applicationFiltersStore.startDate;
        const end = this.applicationFiltersStore.endDate;
        if (!start || !end) return undefined;

        const dayDifference = moment(end).diff(moment(start), "days");
        const compareStart = moment(start)
            .subtract(dayDifference + 1, "days")
            .startOf("date");
        const compareEnd = moment(end)
            .subtract(dayDifference + 1, "days")
            .endOf("date");
        return { compareStart, compareEnd };
    }

    @action
    setPngCaptureInProgress(value: boolean) {
        this.isPngCaptureInProgress = value;
    }

    @action
    private configureGrid() {
        if (!this.dataGridStore) return;
        this.dataGridStore.disableLocalConfig = true;
        this.dataGridStore.checkboxSelection = false;
        this.dataGridStore.hideFilter = true;
        this.dataGridStore.hideHeader = true;
        this.dataGridStore.disableRowSelectionOnClick = true;
        this.dataGridStore.pagination = true;
        this.dataGridStore.paginationMode = "server";
        this.dataGridStore.pageSizeOptions = [10];
        this.dataGridStore.paginationModel = {
            page: 0,
            pageSize: 10,
        };
        this.dataGridStore.onPaginationModelChange = (model) => {
            this.dataGridStore!.paginationModel = model;
            void this.runRefresh();
        };
        // Columns are set dynamically in getData() from the backend response
    }

    /**
     * Builds the request payload for fetching table data using current filters.
     * Mirrors chart request construction to keep drilldowns aligned.
     */
    @action
    private buildRequestState({
        includePagination = true,
        includeRequestId = true,
    }: {
        includePagination?: boolean;
        includeRequestId?: boolean;
    } = {}): SignalsRequest {
        const dateStore = this.applicationFiltersStore.datePickerStore!;
        const filters = this.getMergedFiltersForRequest();
        const startDate = (filters.startDate as string | undefined) ?? "";
        const endDate = (filters.endDate as string | undefined) ?? "";
        const dateReferenceOption =
            filters.dateReferenceOption ?? dateStore.referenceOption;
        const { compareStart, compareEnd } =
            this.getComparePreviousPeriodForRange(
                startDate,
                endDate,
                dateReferenceOption,
            );

        const state: SignalsRequest = {
            ...filters,
            startDate: startDate ?? "",
            endDate: endDate ?? "",
            dateReference: dateReferenceOption,
            dateReferenceOption,
            dateRangeLabel:
                this.applicationFiltersStore.dateRangeLabel ??
                filters.dateRangeLabel,
        };

        if (this.config.capabilities.comparePrev && this.comparePrevEnabled) {
            state.previousPeriodStartDate = compareStart;
            state.previousPeriodEndDate = compareEnd;
        }

        const selectedIds = this.selectedIds ?? {};

        if (selectedIds.extractionLlmClassifier) {
            state.llmClassifierId = selectedIds.extractionLlmClassifier;
        }
        if (selectedIds.extendedMetadataObjectClassifier) {
            state.extendedMetadataClassifierId =
                selectedIds.extendedMetadataObjectClassifier;
        }

        const daysInRange =
            startDate && endDate ? moment(endDate).diff(startDate, "days") : 0;

        const pagination = this.getPaginationState();
        if (includePagination) {
            state.pageNumber = pagination.pageNumber;
            state.pageSize = pagination.pageSize;
        }

        state.aggregateDataByTemporalGrain = daysInRange > THE_MICHAEL_GRAIN;
        state.requestAiSummary = false;
        if (includeRequestId) {
            state.requestId = uuidv4();
        }
        return state;
    }

    public preRefresh(): void {
        this.state = this.buildRequestState({
            includePagination: true,
            includeRequestId: false,
        });
    }

    private getPaginationState() {
        const model = this.dataGridStore?.paginationModel;
        const page = model?.page ?? 0;
        const pageSizeFromModel = model?.pageSize;
        const pageSizeFromOptions = this.dataGridStore?.pageSizeOptions?.[0];
        const pageSize =
            pageSizeFromModel ??
            (typeof pageSizeFromOptions === "number"
                ? pageSizeFromOptions
                : pageSizeFromOptions?.value) ??
            10;

        return {
            pageNumber: page + 1,
            pageSize,
        };
    }

    protected override getCacheFiltersPart(): unknown {
        return this.buildRequestState({
            includePagination: false,
            includeRequestId: false,
        });
    }

    protected override getCacheSelectionsPart(): unknown {
        return undefined;
    }

    protected override getCacheExtraParts(): unknown {
        return this.getPaginationState();
    }

    protected override getCachePayload(): TableCachePayload | undefined {
        if (!this.dataGridStore) return;

        const paginationModel = this.dataGridStore.paginationModel;
        return {
            sqlQuery: this.sqlQuery,
            rows: this.data ?? [],
            pagination: {
                pageNumber: paginationModel.page,
                pageSize: paginationModel.pageSize,
                totalCount: this.dataGridStore.rowCount ?? 0,
            },
            columns: this.dataGridStore?.columns,
        };
    }

    protected override restoreFromCachePayload(
        cachePayload: TableCachePayload,
    ): void {
        if (!this.dataGridStore) return;
        if (!cachePayload) return;

        const rows = Array.isArray(cachePayload.rows) ? cachePayload.rows : [];
        this.sqlQuery = cachePayload.sqlQuery;

        this.dataGridStore.paginationModel = {
            page: cachePayload.pagination.pageNumber,
            pageSize: cachePayload.pagination.pageSize,
        };
        this.dataGridStore.pageSizeOptions = [cachePayload.pagination.pageSize];

        this.dataGridStore.rowCount = cachePayload.pagination.totalCount;
        this.dataGridStore.setRows(rows as any[]);
        this.dataGridStore.setAllRows(rows as any[]);
        this.dataGridStore.setColumns(cachePayload.columns);

        if (this.config.dataGrid?.dataGridBuilder) {
            this.config.dataGrid.dataGridBuilder(
                this.dataGridStore,
                this.config.dataGrid,
                rows as any,
                this as any,
            );
        }
    }

    /**
     * Fetches table data for endpoints treated as tables (e.g., Trending Topics).
     * The backend returns columns and rows co-located in a SignalsTableResponse.
     */
    public async getData({
        abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<SignalTableDatum[]> {
        const state = this.buildRequestState({
            includePagination: true,
            includeRequestId: true,
        });
        this.state = state;

        if (!this.dataEndpoint) return [];
        this.signalsService.setAbortSignal(abortSignal);

        if (this.dataGridStore) {
            this.dataGridStore.setLoading(true);
        }

        const currentPage = this.getPaginationState().pageNumber;

        try {
            const response = await this.signalsService.getTableData(
                state,
                this.dataEndpoint,
                currentPage,
            );

            this.sqlQuery = response.sqlQuery;

            const pagination = response.pagination;
            const rows = response.rows as any[];

            if (this.dataGridStore) {
                // Set columns from backend response through the formatter registry.
                // Interactive formatters (e.g. topicDrilldownCell) receive the store
                // so they can select a Topic or Driver and open the point menu from the cell.
                const colDefs = response.columns.map((col) =>
                    toGridColDef(col, { store: this }),
                );
                this.dataGridStore.setColumns(colDefs);

                this.dataGridStore.paginationModel = {
                    page: (pagination.pageNumber ?? currentPage) - 1,
                    pageSize:
                        pagination.pageSize ??
                        this.dataGridStore.paginationModel?.pageSize ??
                        10,
                };
                this.dataGridStore.pageSizeOptions = [
                    this.dataGridStore.paginationModel.pageSize,
                ];
                this.dataGridStore.rowCount = pagination.totalCount;
                this.dataGridStore.setRows(rows);
                this.dataGridStore.setAllRows(rows);
            }

            return rows;
        } finally {
            if (this.dataGridStore) {
                this.dataGridStore.setLoading(false);
            }
        }
    }

    @action
    toggleRenderMode() {
        this.currentRenderMode =
            this.currentRenderMode === RenderModeType.Count
                ? RenderModeType.Percent
                : RenderModeType.Count;
        void this.runRefresh();
    }

    @action
    toggleComparePrevious() {
        this.comparePrevEnabled = !this.comparePrevEnabled;
        void this.runRefresh();
    }

    @action
    setSelectedId(key: string, value: any) {
        this.selectedIds = { ...(this.selectedIds ?? {}), [key]: value };
    }

    @action
    selectTopicRow(row: any) {
        if (!this.isPointMenuEnabled || !this.drilldownStore) return null;

        const selectedTopic = row?.topicName;
        const topicNumber =
            row?.topicNumber != null ? Number(row.topicNumber) : Number.NaN;

        if (!selectedTopic || Number.isNaN(topicNumber)) return null;

        const themeNumber = row?.themeNumber;
        const topicCount = row?.volume != null ? Number(row.volume) : null;

        this.drilldownStore.clearSelection();
        this.drilldownStore.selectTopic({
            selectedTopic,
            topicNumber,
            themeNumber,
            x: selectedTopic,
            count: topicCount,
        });

        return this.drilldownStore.selectedTopic;
    }

    /**
     * Driver counterpart of {@link selectTopicRow}, for Trending Drivers. Kept separate because
     * the two methods read different row fields and resolve different
     * selections, and a Driver row has no topicNumber to fall back on.
     *
     * Resolves the existing SelectedDriver rather than a generic data point, which is what the
     * Theme Cards' View Drivers flow already produces — so View Data and View in Conversations
     * work through the paths that flow already established, with no second Driver mechanism.
     */
    @action
    selectDriverRow(row: any) {
        if (!this.isPointMenuEnabled || !this.drilldownStore) return null;

        const driverName = row?.driverName;
        const driverIndex =
            row?.driverIndex != null ? Number(row.driverIndex) : Number.NaN;

        if (!driverName || Number.isNaN(driverIndex)) return null;

        this.drilldownStore.clearSelection();
        this.drilldownStore.selectDriver({
            selectedDriver: driverName,
            driverIndex,
            themeNumber: row?.themeNumber || undefined,
            themeId: row?.themeId || undefined,
            x: driverName,
            count: row?.volume != null ? Number(row.volume) : null,
        });

        return this.drilldownStore.selectedDriver;
    }

    @action
    openPointMenu(anchor: { top: number; left: number }) {
        this.pointMenuAnchor = anchor;
    }

    @action
    closePointMenu() {
        this.pointMenuAnchor = null;
    }

    @action
    openJsonCellViewer(value: unknown, title: string) {
        this.jsonCellViewer = { isOpen: true, title, value };
    }

    @action
    closeJsonCellViewer() {
        this.jsonCellViewer = { ...this.jsonCellViewer, isOpen: false };
    }

    // === CSV/Export ===
    @observable
    exportableFields: SignalsModels.ExportableFieldsResponse | null = null;
    @observable exportableFieldsLoading = false;
    @observable private exportableFieldsClassifierId: string | undefined =
        undefined;

    @computed
    get allowColumnSelection(): boolean {
        return (
            this.config.capabilities.csvExport === "server-export" &&
            !!this.config.exportConfig?.allowColumnSelection
        );
    }

    @computed
    get exportableFieldNodes(): FieldNode[] {
        if (!this.exportableFields) return [];

        return this.exportableFields.flatMap((group) => {
            if (!Array.isArray(group.fields)) return [];

            return {
                key: SignalsModels.getExportableFieldCategoryString(
                    group.category,
                ),
                label: SignalsModels.getExportableFieldCategoryString(
                    group.category,
                ),
                path: [],
                children: group.fields.map((field) => ({
                    key: field.name,
                    label: field.label,
                    path: field.name.split("."),
                })),
            };
        });
    }

    @action
    async loadExportableFields() {
        const classifierId = this.selectedIds
            .extendedMetadataObjectClassifier as string | undefined;
        if (!classifierId) return;

        if (
            this.exportableFields &&
            this.exportableFieldsClassifierId === classifierId
        )
            return;
        if (this.exportableFieldsLoading) return;

        this.exportableFieldsLoading = true;
        try {
            this.exportableFields =
                await this.signalsService.getExtractedConversationExportableFields(
                    classifierId,
                );
            this.exportableFieldsClassifierId = classifierId;
        } catch (_error) {
            this.exportableFields = null;
            this.exportableFieldsClassifierId = undefined;
            this.rootStore
                .getStore(MessageStore)
                .logError("Unable to load exportable fields.");
        } finally {
            this.exportableFieldsLoading = false;
        }
    }

    async exportCsv(selectedColumns?: string[]) {
        if (!this.dataEndpoint) return null;
        if (this.config.capabilities.csvExport !== "server-export") return null;

        const state = this.buildRequestState({ includePagination: false });
        return this.signalsService.getChartExport(
            this.dataEndpoint,
            state,
            selectedColumns,
        );
    }

    public async updateTableConfiguration({
        title,
        subtitle,
        objectFilters,
        vizOptions,
    }: {
        title?: string;
        subtitle?: string;
        objectFilters?: string | null;
        vizOptions?: SignalsParsedVizOptions;
    }): Promise<boolean> {
        const currentVizSignature = JSON.stringify(this.parsedVizOptions ?? {});
        const nextVizSignature = JSON.stringify(vizOptions ?? {});
        const vizOptionsChanged =
            vizOptions !== undefined &&
            currentVizSignature !== nextVizSignature;

        try {
            // isEditable means either its a standard dashboard (you can edit those selections and such) OR you have edit permission. isSaveable means you have to have permission to edit.
            // BOTH are required to actually be able to save changes to the backend, otherwise we just apply them locally for the user's current session.
            if (this.isEditable && this.isSaveable) {
                await this.signalsService.updateSignalsTableCard({
                    id: this.id,
                    title,
                    subtitle,
                    objectFilters,
                    vizOptions: vizOptions
                        ? JSON.stringify(vizOptions)
                        : undefined,
                });
            }

            if (title !== undefined) {
                this.title = title;
            }
            if (subtitle !== undefined) {
                this.subtitle = subtitle;
            }
            if (vizOptionsChanged) {
                this.setParsedVizOptions(vizOptions ?? null);
                this.hasResolvedInitialSelectionDefaults = false;
                await this.runRefresh();
            }

            return true;
        } catch (_error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to update card settings.");
            return false;
        }
    }

    private initializeSelectionDefaults() {
        if (!this.canAutoRefreshInCurrentContext()) {
            return;
        }

        const resolution = this.resolveSelectionDefaultsForRefresh();

        if (resolution.status === "waiting") {
            this.scheduleStandaloneSelectionDefaultsFollowup(
                resolution.waitFor,
            );
        }
    }

    @action
    private resolveSelectionDefaultsForRefresh():
        | { status: "ready" }
        | { status: "waiting"; waitFor: Promise<void> } {
        const resolution = resolveSelectionDefaults(
            this.buildResolverContext(),
        );

        if (resolution.status === "waiting") {
            const waitFor = this.requestSelectionDefaultDependencies(
                resolution.dependencies,
            );

            this.waitingSelectionDefaultDependencies = [
                ...resolution.dependencies,
            ];

            return {
                status: "waiting",
                waitFor,
            };
        }

        this.waitingSelectionDefaultDependencies = [];

        this.suppressDefaultPersistence = true;
        try {
            for (const [key, value] of Object.entries(resolution.patch)) {
                if (value !== undefined) {
                    this.setSelectedId(key, value);
                }
            }
        } finally {
            this.suppressDefaultPersistence = false;
        }

        this.hasResolvedInitialSelectionDefaults = true;

        return { status: "ready" };
    }

    // The entire default selection setup is copied from SignalsChartCard. Ideally it lives
    // as a separate store or on the base object.
    private buildResolverContext(): SelectionDefaultsResolverContext {
        return {
            selectionType: this.selectionType,
            chartType: undefined as any,
            dataEndpoint: this.dataEndpoint,
            maxSelection: 1,
            autoSelectionLimit: 1,
            preferVizDefaults: !this.hasResolvedInitialSelectionDefaults,
            vizDefaultSelectedIds: this.normalizeVizDefaultSelectedIds(
                this.parsedVizOptions?.defaultSelectedIds,
            ),
            selectedIds: {
                extractionLlmClassifier: this.selectedIds
                    .extractionLlmClassifier as string | undefined,
                extendedMetadataObjectClassifier: this.selectedIds
                    .extendedMetadataObjectClassifier as string | undefined,
            },
            attemptedDependencies: this.requestedSelectionDefaultDependencies,
            options: {
                orgModules: [],
                orgScoredModules: [],
                userClassifiers: [],
                classifierCategories: [],
                llmClassifiers: [],
                customerTypes: [],
                topics: [],
                themes: [],
                hipaaOptions: [],
                extractionLlmClassifiers:
                    this.optionsStore.extractionLlmClassifiers,
                extendedMetadataObjectClassifiers:
                    this.optionsStore.extendedMetadataObjectClassifiers,
            },
            loading: {
                orgModulesLoading: false,
                userClassifiersLoading: false,
                classifierCategoriesLoading: false,
                llmClassifiersLoading: false,
                customerTypesLoading: false,
                hipaaOptionsLoading: false,
                extractionLlmClassifiersLoading:
                    this.optionsStore.extractionLlmClassifiersLoading,
                extendedMetadataObjectClassifiersLoading:
                    this.optionsStore.extendedMetadataObjectClassifiersLoading,
            },
            capabilities: {
                canSelectAllModules: false,
            },
        };
    }

    private requestSelectionDefaultDependencies(
        dependencies: SelectionDefaultDependency[],
    ): Promise<void> {
        const uniqueDependencies = Array.from(new Set(dependencies));
        const waitPromises = uniqueDependencies.map((dependency) =>
            this.requestSelectionDefaultDependency(dependency),
        );
        return Promise.all(waitPromises).then(() => undefined);
    }

    private requestSelectionDefaultDependency(
        dependency: SelectionDefaultDependency,
    ): Promise<void> {
        const existingPromise =
            this.selectionDefaultDependencyPromises.get(dependency);
        if (existingPromise) {
            return existingPromise;
        }

        this.requestedSelectionDefaultDependencies.add(dependency);

        const loadPromise = (async () => {
            switch (dependency) {
                case "extractionLlmClassifiers":
                    await Promise.all([
                        this.optionsStore.refreshSlimExtractionLLMClassifiers(),
                        this.optionsStore.refreshExtendedMetadataObjectClassifiers(),
                    ]);
                    break;
                default:
                    break;
            }
        })()
            .catch(() => undefined)
            .finally(() => {
                this.selectionDefaultDependencyPromises.delete(dependency);
            });

        this.selectionDefaultDependencyPromises.set(dependency, loadPromise);
        return loadPromise;
    }

    private scheduleStandaloneSelectionDefaultsFollowup(
        waitFor: Promise<void>,
    ) {
        if (this.pendingStandaloneDefaultFollowup) {
            return;
        }

        this.pendingStandaloneDefaultFollowup = waitFor.finally(() => {
            this.pendingStandaloneDefaultFollowup = null;
            if (!this.isRefreshing && this.canAutoRefreshInCurrentContext()) {
                void this.runRefresh();
            }
        });
    }

    // === Default selection persistence ===

    protected override getSelectedIdsForVizDefaults(): string[] {
        switch (this.selectionType) {
            case "LLM Classifier Extracted Metadata": {
                const ids: string[] = [];
                if (this.selectedIds.extractionLlmClassifier) {
                    ids.push(
                        this.selectedIds.extractionLlmClassifier as string,
                    );
                }
                if (this.selectedIds.extendedMetadataObjectClassifier) {
                    ids.push(
                        this.selectedIds
                            .extendedMetadataObjectClassifier as string,
                    );
                }
                return ids;
            }
            default:
                return [];
        }
    }

    @action
    async updateDefaultSelectedIds() {
        if (!this.isEditable || !this.isSaveable) {
            return;
        }

        if (this.suppressDefaultPersistence) {
            return;
        }

        if (this.isWaitingOnSelectionDefaults) {
            return;
        }

        const currentSelectedIds = this.getSelectedIdsForVizDefaults();

        const defaultSelectedIds = this.normalizeVizDefaultSelectedIds(
            this.parsedVizOptions?.defaultSelectedIds,
        );

        const arraysEqual = (a: string[], b: string[]) => {
            if (a.length !== b.length) return false;
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.every((val, idx) => val === sortedB[idx]);
        };

        if (arraysEqual(currentSelectedIds, defaultSelectedIds)) {
            return;
        }

        const updatedVizOptions: SignalsParsedVizOptions = {
            ...(this.parsedVizOptions ?? {}),
            defaultSelectedIds: currentSelectedIds,
        };

        if (!this.dataEndpoint) {
            return;
        }

        try {
            await this.signalsService.updateSignalsTableCard({
                id: this.id,
                vizOptions: JSON.stringify(updatedVizOptions),
            });

            this.setParsedVizOptions(updatedVizOptions);
        } catch (_error) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Failed to save selection defaults.");
        }
    }

    private normalizeVizDefaultSelectedIds(value: unknown): string[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value.map((id) => `${id}`.trim()).filter((id) => id.length > 0);
    }

    private getFormattedDates(): (string | undefined)[] {
        const dateStore = this.applicationFiltersStore.datePickerStore!;
        const filters = this.getMergedFiltersForRequest();
        const startDate = (filters.startDate as string | undefined) ?? "";
        const endDate = (filters.endDate as string | undefined) ?? "";
        const dateReferenceOption =
            filters.dateReferenceOption ?? dateStore.referenceOption;
        const { compareStart, compareEnd } =
            this.getComparePreviousPeriodForRange(
                startDate,
                endDate,
                dateReferenceOption,
            );

        return [startDate, endDate, compareStart, compareEnd];
    }

    private getComparePreviousPeriodForRange(
        startDate?: string,
        endDate?: string,
        dateReferenceOption = this.applicationFiltersStore.datePickerStore!
            .referenceOption,
    ): {
        compareStart?: string;
        compareEnd?: string;
    } {
        if (!startDate || !endDate) {
            return {
                compareStart: undefined,
                compareEnd: undefined,
            };
        }

        const start = moment(startDate);
        const end = moment(endDate);
        const dayDifference = end.diff(start, "days");
        const compareStartMoment = moment(start)
            .subtract(dayDifference + 1, "days")
            .startOf("date");
        const compareEndMoment = moment(end)
            .subtract(dayDifference + 1, "days")
            .endOf("date");

        const compareDateRange = toIsoByDateReference(
            compareStartMoment,
            compareEndMoment,
            dateReferenceOption,
        );

        return {
            compareStart: compareDateRange.beginDate,
            compareEnd: compareDateRange.endDate,
        };
    }
}

type SelectedIdKey =
    | "extractionLlmClassifier"
    | "extendedMetadataObjectClassifier";

export default TableCardStore;
